import React, { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { LineChart as LineIcon, Loader2, Info, Coffee } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const COLORS = ["#0ea5e9", "#ec4899", "#10b981", "#8b5cf6", "#ef4444"];
const SELF_COLOR = "#9f1239";
const SELF_KEY = "__self";
const BF_COLOR = "#d97706";

// Pallino del grafico: arancio con bordo spesso se il prezzo include la colazione.
const makeDot = (s) => (props) => {
  const { cx, cy, payload, key } = props;
  if (cx == null || cy == null || payload?.[s.key] == null) return <g key={key} />;
  const cell = s.cells[payload.date];
  const bf = cell?.breakfast;
  if (cell?.estimate) return <circle key={key} cx={cx} cy={cy} r={3} fill="hsl(var(--card))" stroke={s.color} strokeWidth={1.5} />;
  return bf
    ? <circle key={key} cx={cx} cy={cy} r={4} fill={BF_COLOR} stroke={s.color} strokeWidth={2} />
    : <circle key={key} cx={cx} cy={cy} r={2} fill={s.color} />;
};

const fmtD = (iso, lang) => {
  try { return new Date(iso + "T00:00:00").toLocaleDateString(lang === "en" ? "en-US" : "it-IT", { day: "numeric", month: "short" }); }
  catch { return iso; }
};

function ChartTooltip({ active, payload, label, series, lang }) {
  if (!active || !payload?.length) return null;
  const L = (it, en) => (lang === "en" ? en : it);
  return (
    <div className="rounded-md border border-border bg-card px-3 py-2 text-xs shadow-lg" data-testid="future-prices-tooltip">
      <p className="font-semibold mb-1">{fmtD(label, lang)}</p>
      {payload.map((p) => {
        const s = series.find((x) => x.key === p.dataKey);
        const cell = s?.cells?.[label];
        if (!cell) return null;
        return (
          <div key={p.dataKey} className="flex items-baseline gap-2 py-0.5">
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: p.color }} />
            <span className="truncate max-w-[150px]">{s.name}</span>
            <b className="ml-auto font-data">€{cell.price_net}</b>
            <span className="text-muted-foreground">{L("lordo", "gross")} €{Math.round(cell.price)}</span>
            {cell.estimate && <span className="italic text-muted-foreground">{L("stima", "est.")}</span>}
            {cell.breakfast && <span className="inline-flex items-center gap-0.5 font-semibold" style={{ color: BF_COLOR }}><Coffee className="w-3 h-3" />{L("colaz.", "bkfst")}</span>}
            {cell.occupancy_est != null && <span className="text-muted-foreground">· {cell.sold_out ? "SO" : `${cell.occupancy_est}%`}</span>}
          </div>
        );
      })}
    </div>
  );
}

export default function CompetitorFuturePrices({ propId, lang = "it", refreshKey = 0 }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!propId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/properties/${propId}/competitor-occupancy-grid?lang=${lang}`);
      setData(data);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [propId, lang]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const allDates = data?.dates || [];
  const series = useMemo(() => {
    const comps = (data?.competitors || []).map((c, i) => ({ key: c.competitor_id, name: c.name, cells: c.cells || {}, color: COLORS[i % COLORS.length], pct: c.calibration_pct }));
    const self = data?.self;
    if (self && Object.keys(self.cells || {}).length) {
      comps.push({ key: SELF_KEY, name: L(`${self.name} (tu)`, `${self.name} (you)`), cells: self.cells, color: SELF_COLOR, pct: self.calibration_pct, isSelf: true });
    }
    return comps;
  }, [data, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  // Solo le date con almeno un prezzo letto (le altre sarebbero colonne vuote).
  const dates = useMemo(() => allDates.filter((iso) => series.some((s) => s.cells[iso]?.price_net != null)), [allDates, series]);

  const chartData = useMemo(() => dates.map((iso) => {
    const row = { date: iso };
    series.forEach((s) => { const c = s.cells[iso]; if (c?.price_net != null) row[s.key] = c.price_net; });
    return row;
  }), [dates, series]);

  return (
    <motion.div data-testid="competitor-future-prices" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="mb-6 rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <LineIcon className="w-4 h-4 text-primary" />
          <h4 className="text-sm font-semibold">{L("Prezzi futuri competitor — 6 mesi (netto)", "Competitor future prices — 6 months (net)")}</h4>
        </div>
        <span className="text-[11px] text-muted-foreground">{L("Netto = lordo − tuo sconto per competitor", "Net = gross − your per-competitor discount")}</span>
      </div>
      <p className="text-[11px] text-muted-foreground mb-3">
        {L("Per ogni data mostra il prezzo e l'occupazione PIÙ RECENTI tra tutte le scansioni (qualsiasi tipo). Si aggiorna da solo a ogni nuova scansione.",
           "For each date shows the MOST RECENT price and occupancy across all scans (any type). Updates automatically with every new scan.")}
      </p>

      {loading && !data ? (
        <div className="py-8 text-center text-muted-foreground text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{L("Carico i prezzi…", "Loading prices…")}</div>
      ) : !dates.length || !series.length ? (
        <div className="py-6 text-center text-sm text-muted-foreground" data-testid="future-prices-empty">
          <Info className="w-4 h-4 inline mr-1.5" />
          {L("Nessun prezzo futuro ancora. Seleziona i 5 competitor e avvia una scansione.", "No future prices yet. Select your 5 competitors and run a scan.")}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mb-2 text-[11px]" data-testid="future-prices-legend">
            {series.map((s) => (
              <span key={s.key} className={`inline-flex items-center gap-1.5 ${s.isSelf ? "font-semibold" : ""}`}>
                <span className="inline-block w-3 h-0.5 rounded" style={{ background: s.color }} />{s.name}
              </span>
            ))}
            <span className="inline-flex items-center gap-1.5 font-semibold" style={{ color: BF_COLOR }} data-testid="future-prices-breakfast-legend">
              <Coffee className="w-3.5 h-3.5" />{L("= prezzo con colazione inclusa", "= price includes breakfast")}
            </span>
            {series.some((x) => x.isSelf && Object.values(x.cells).some((c) => c.estimate)) && (
              <span className="inline-flex items-center gap-1.5 text-muted-foreground" data-testid="future-prices-estimate-legend">
                <span className="inline-block w-2.5 h-2.5 rounded-full border-2" style={{ borderColor: SELF_COLOR }} />
                {L("= tuo prezzo «a partire da» dalla ricerca città (stima, dove manca la lettura diretta)", "= your 'from' price from the city search (estimate, where no direct read)")}
              </span>
            )}
          </div>
          <div className="h-72 w-full" data-testid="future-prices-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tickFormatter={(v) => fmtD(v, lang)} tick={{ fontSize: 10 }} minTickGap={24} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `€${v}`} width={48} domain={["auto", "auto"]} />
                <Tooltip content={<ChartTooltip series={series} lang={lang} />} />
                {series.map((s) => (
                  <Line key={s.key} type="monotone" dataKey={s.key} name={s.name} stroke={s.color} connectNulls
                    strokeWidth={s.isSelf ? 3 : 1.8} strokeDasharray={s.isSelf ? "6 3" : undefined} dot={makeDot(s)} activeDot={{ r: 5 }} isAnimationActive={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <PriceTable dates={dates} series={series} lang={lang} />
        </>
      )}
    </motion.div>
  );
}

function PriceTable({ dates, series, lang }) {
  const L = (it, en) => (lang === "en" ? en : it);
  return (
    <div className="overflow-x-auto mt-4" data-testid="future-prices-table">
      <table className="border-separate text-[11px]" style={{ borderSpacing: 0 }}>
        <thead>
          <tr>
            <th className="sticky left-0 z-20 bg-card text-left font-medium text-muted-foreground px-2 py-1" style={{ minWidth: 170 }}>{L("Struttura", "Property")}</th>
            {dates.map((iso) => <th key={iso} className="font-normal text-muted-foreground px-1 py-1 text-center whitespace-nowrap" style={{ minWidth: 52 }}>{fmtD(iso, lang)}</th>)}
          </tr>
        </thead>
        <tbody>
          {series.map((s) => (
            <tr key={s.key} data-testid={`future-row-${s.isSelf ? "self" : s.key}`}>
              <td className="sticky left-0 z-10 bg-card px-2 py-1 border-t border-border truncate" style={{ minWidth: 170, maxWidth: 210 }} title={s.name}>
                <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: s.color }} />
                <span className={s.isSelf ? "font-semibold" : ""}>{s.name}</span>
                <span className="block text-[10px] text-muted-foreground">{s.pct != null ? `${L("sconto", "discount")} ${s.pct > 0 && !s.isSelf ? "+" : s.isSelf ? "−" : ""}${s.pct}%` : L("nessuno sconto impostato", "no discount set")}</span>
              </td>
              {dates.map((iso) => {
                const c = s.cells[iso];
                return (
                  <td key={iso} className={`px-1 py-1 border-t border-border text-center ${c?.breakfast ? "bg-amber-100/70 dark:bg-amber-900/30" : ""}`} data-testid={`future-cell-${s.isSelf ? "self" : s.key}-${iso}`}
                    title={c?.detected_at || c?.as_of ? `${L("Aggiornato", "Updated")}: ${new Date(c.detected_at || c.as_of).toLocaleString()}` : ""}>
                    {c?.price_net != null ? (
                      <>
                        <div className="font-data font-semibold inline-flex items-center gap-0.5">
                          {c.breakfast && <Coffee className="w-3 h-3" style={{ color: BF_COLOR }} data-testid={`future-bf-${s.key}-${iso}`} />}€{c.price_net}
                        </div>
                        <div className="text-[10px] text-muted-foreground">€{Math.round(c.price)}{c.estimate && <i data-testid={`future-est-${iso}`}> · {L("stima", "est.")}</i>}</div>
                      </>
                    ) : c?.unavailable ? (
                      <span className="text-[10px] text-muted-foreground italic" data-testid={`future-unavail-${iso}`} title={L("Non prenotabile per 1 notte su Booking (soggiorno minimo o tutto venduto)", "Not bookable for 1 night on Booking (min stay or sold out)")}>{L("non disp.", "n/a")}</span>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-[11px] text-muted-foreground mt-2">
        {L("In alto il prezzo NETTO (lordo meno il tuo sconto), sotto il LORDO letto su Booking. Celle arancio con la tazzina = prezzo con colazione inclusa. Il tuo prezzo usa il tuo sconto manuale. Dati dal database (nessuna chiamata Apify).",
           "Top: NET price (gross minus your discount), below: GROSS read on Booking. Orange cells with the cup = price includes breakfast. Your price uses your manual discount. Data from the database (no Apify calls).")}
      </p>
    </div>
  );
}
